// All bulk map data lives in the Origin Private File System. A single large
// file per pack is dramatically cheaper to write and read than hundreds of
// thousands of IndexedDB rows, and OPFS is available on both target platforms
// (Chrome/Android, Safari 15.2+).

const PACKS_DIR = "packs";

export function opfsSupported(): boolean {
  return typeof navigator !== "undefined" && !!navigator.storage?.getDirectory;
}

async function packsDirectory(): Promise<FileSystemDirectoryHandle> {
  if (!opfsSupported()) {
    throw new Error("This browser has no Origin Private File System support.");
  }
  const root = await navigator.storage.getDirectory();
  return root.getDirectoryHandle(PACKS_DIR, { create: true });
}

export async function listPackFiles(): Promise<Map<string, number>> {
  const dir = await packsDirectory();
  const sizes = new Map<string, number>();
  // @ts-expect-error - entries() is present on all engines that ship OPFS.
  for await (const [name, handle] of dir.entries()) {
    if (handle.kind === "file") {
      sizes.set(name, (await handle.getFile()).size);
    }
  }
  return sizes;
}

export async function readPackFile(name: string): Promise<File | null> {
  const dir = await packsDirectory();
  try {
    return await (await dir.getFileHandle(name)).getFile();
  } catch {
    return null;
  }
}

export async function openPackForWrite(
  name: string,
  { keepExisting }: { keepExisting: boolean },
): Promise<FileSystemWritableFileStream> {
  const dir = await packsDirectory();
  const handle = await dir.getFileHandle(name, { create: true });
  return handle.createWritable({ keepExistingData: keepExisting });
}

export async function deletePackFile(name: string): Promise<void> {
  const dir = await packsDirectory();
  await dir.removeEntry(name).catch(() => undefined);
}

export interface StorageBudget {
  quota: number;
  usage: number;
  available: number;
  persisted: boolean;
}

export async function storageBudget(): Promise<StorageBudget> {
  const estimate = (await navigator.storage?.estimate?.()) ?? {};
  const quota = estimate.quota ?? 0;
  const usage = estimate.usage ?? 0;
  const persisted = (await navigator.storage?.persisted?.()) ?? false;
  return { quota, usage, available: Math.max(0, quota - usage), persisted };
}

/**
 * Installed PWAs are normally granted persistent storage without a prompt on
 * Chrome; Safari manages durability itself and simply resolves false.
 */
export async function requestPersistence(): Promise<boolean> {
  if (!navigator.storage?.persist) return false;
  if (await navigator.storage.persisted()) return true;
  try {
    return await navigator.storage.persist();
  } catch {
    return false;
  }
}
